// The canvas compositing loop behind FR-RD1's "screen + camera overlay" and FR-RD3's
// draggable, filterable camera. Lifted out of useRecorder.ts, which now only owns devices and
// MediaRecorder; the pure geometry it leans on is cameraFx.ts.
//
// Why a canvas at all (unchanged from the original overlay): neither getDisplayMedia nor
// getUserMedia can produce a composited track, so both raw tracks are decoded into hidden
// <video> elements, drawn into a <canvas> every frame, and MediaRecorder is handed the
// canvas's own captureStream() track. Everything downstream treats it as an opaque
// MediaStreamTrack.
//
// What changed for FR-RD3: camera-only mode is composited too, and settings live in a mutable
// object the draw loop re-reads every frame. That second point is the whole design. Dragging
// the bubble or switching a filter **while recording** must not rebuild the track — swapping
// the video track under a running MediaRecorder is not something the API supports, and
// re-requesting the source would re-prompt getDisplayMedia's screen picker mid-recording. So
// settings mutate; the pipeline does not.
//
// Camera-only mode is composited only when an effect is chosen before recording; with none,
// useRecorder records the camera track directly (and the effect is locked until Stop), because
// the canvas path proved too slow on some Linux GPU stacks (2026-10-26).
//
// On desktop Linux the canvases are CPU-backed (`willReadFrequently: true`, see
// `preferCpuCanvas`). There a GPU-backed canvas made every captured frame a GPU→CPU readback
// before encoding, and the encoder fell behind real time: the backlog queued inside
// MediaRecorder and was lost when the renderer died. Compositing in CPU memory costs CPU per
// frame (a 1246×1440 screen + camera ran at ~21 fps instead of 30, but in real time) and
// leaves nothing to read back. Elsewhere the canvases stay GPU-backed: Chrome's readback is
// normally fast on macOS and Windows, and software compositing of a 4K screen could cost
// more there than it saves. Untested on those platforms, so deliberately unchanged.

import {
  BACKGROUND_BLUR_FRACTION,
  CATEGORY_PERSON,
  MASK_FEATHER_FRACTION,
  cameraFilterOption,
  clampBubble,
  filterNeedsSegmentation,
  type CameraBubble,
  type CameraFilterId,
} from "./cameraFx";
// Type-only, so nothing about MediaPipe reaches this module's chunk. The implementation is
// pulled in by `import("./segmenter")` below, the first time a user actually turns blur on.
import type { Segmenter } from "./segmenter";

export const OVERLAY_CAPTURE_FPS = 30;

/** What the UI needs to say about background blur. `unavailable` is not an error state the
 * user must act on — the camera simply renders unfiltered. */
export type BlurState = "off" | "loading" | "ready" | "unavailable";

export interface CompositorSettings {
  bubble: CameraBubble;
  filter: CameraFilterId;
}

export interface CompositorOptions {
  canvas: HTMLCanvasElement;
  /** `null` in camera-only mode — the camera then fills the canvas instead of being a bubble. */
  screenVideo: HTMLVideoElement | null;
  cameraVideo: HTMLVideoElement;
  settings: CompositorSettings;
  onBlurState?: (state: BlurState) => void;
  /** Called once per frame: `true` if it drew, `false` if the camera had nothing to draw
   * (dev-mode diagnostics, diagnostics.ts). */
  onDraw?: (drawn: boolean) => void;
}

export function waitForVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();
  return new Promise((resolve) => {
    video.addEventListener("loadedmetadata", () => resolve(), { once: true });
  });
}

/** Desktop Linux only (not Android, whose user agent also says "Linux"): see the module doc.
 * `nav` is injectable for tests. */
export function preferCpuCanvas(
  nav: { userAgent: string; userAgentData?: { platform?: string } } = navigator as never,
): boolean {
  const platform = nav.userAgentData?.platform;
  if (platform) return platform === "Linux";
  return /Linux/.test(nav.userAgent) && !/Android/.test(nav.userAgent);
}

/** A same-size scratch canvas, reallocated only when the size it is asked for changes —
 * resizing a canvas clears it and reallocates its backing store, so doing it per frame at
 * 30 fps is exactly the kind of churn that shows up as dropped frames in a recording. */
class Scratch {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;

  constructor(cpuBacked: boolean) {
    this.canvas = document.createElement("canvas");
    const ctx = this.canvas.getContext("2d", { willReadFrequently: cpuBacked });
    if (!ctx) throw new Error("2d canvas context unavailable");
    this.ctx = ctx;
  }

  sized(width: number, height: number): CanvasRenderingContext2D {
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    } else {
      this.ctx.clearRect(0, 0, width, height);
    }
    this.ctx.filter = "none";
    this.ctx.globalCompositeOperation = "source-over";
    return this.ctx;
  }
}

export class CameraCompositor {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly screenVideo: HTMLVideoElement | null;
  private readonly cameraVideo: HTMLVideoElement;
  private readonly onBlurState?: (state: BlurState) => void;
  private readonly onDraw?: (drawn: boolean) => void;

  private settings: CompositorSettings;
  private raf: number | null = null;
  private stopped = false;

  private segmenter: Segmenter | null = null;
  private segmenterRequested = false;
  private blurState: BlurState = "off";
  /** The most recent mask, kept between frames: segmentation runs per frame, but a frame whose
   * segmentation fails reuses the previous mask rather than popping to a sharp background. */
  private maskCanvas: HTMLCanvasElement | null = null;

  private readonly cpuBacked = preferCpuCanvas();
  private readonly filtered = new Scratch(this.cpuBacked);
  private readonly person = new Scratch(this.cpuBacked);

  constructor(options: CompositorOptions) {
    this.canvas = options.canvas;
    // CPU-backed on desktop Linux, like the Scratch canvases: see the module doc.
    const ctx = this.canvas.getContext("2d", { willReadFrequently: this.cpuBacked });
    if (!ctx) throw new Error("2d canvas context unavailable");
    this.ctx = ctx;
    this.screenVideo = options.screenVideo;
    this.cameraVideo = options.cameraVideo;
    this.onBlurState = options.onBlurState;
    this.onDraw = options.onDraw;
    this.settings = options.settings;
    this.syncSegmenter();
  }

  get aspect(): number {
    return this.canvas.height > 0 ? this.canvas.width / this.canvas.height : 16 / 9;
  }

  /** Live-applied on the next frame. Never restarts the loop or the captured track. */
  update(partial: Partial<CompositorSettings>): void {
    this.settings = { ...this.settings, ...partial };
    this.syncSegmenter();
  }

  start(): void {
    if (this.raf !== null || this.stopped) return;
    // rAF runs at the display's rate (60 Hz or more), but the canvas is captured at
    // OVERLAY_CAPTURE_FPS: drawing faster only adds GPU work the recording never sees.
    // The 2 ms slack keeps a 60 Hz display on every other frame rather than drifting.
    const minIntervalMs = 1000 / OVERLAY_CAPTURE_FPS - 2;
    let lastDrawAt = -Infinity;
    const draw = (now: number) => {
      if (now - lastDrawAt >= minIntervalMs) {
        lastDrawAt = now;
        this.drawFrame();
      }
      this.raf = requestAnimationFrame(draw);
    };
    this.raf = requestAnimationFrame(draw);
  }

  stop(): void {
    this.stopped = true;
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    // The segmenter itself is deliberately not closed here — segmenter.ts memoizes it across
    // recordings because loading it costs seconds.
    this.segmenter = null;
    this.maskCanvas = null;
  }

  // ---- segmentation ---------------------------------------------------------------------

  private setBlurState(state: BlurState): void {
    if (this.blurState === state) return;
    this.blurState = state;
    this.onBlurState?.(state);
  }

  private syncSegmenter(): void {
    if (!filterNeedsSegmentation(this.settings.filter)) {
      this.setBlurState("off");
      return;
    }
    if (this.segmenter) {
      this.setBlurState("ready");
      return;
    }
    if (this.segmenterRequested) return;
    this.segmenterRequested = true;
    this.setBlurState("loading");
    // Dynamic: MediaPipe is ~320 kB of JS on top of an 11.7 MB wasm binary, and the vast
    // majority of visitors are watching a grackle rather than recording one. A static import
    // puts all of it in the chunk that every page load pays for, which is exactly the
    // first-contentful-paint budget (NFR-P3) that the deleted client transcode pipeline used
    // to split mediabunny out of the main chunk to protect. MediaPipe is now the only heavy
    // dependency left on the recording path, and it stays lazy for the same reason.
    void import("./segmenter")
      .then(({ loadSegmenter }) => loadSegmenter())
      .catch(() => null)
      .then((segmenter) => {
        if (this.stopped) return;
        this.segmenter = segmenter;
        if (!filterNeedsSegmentation(this.settings.filter)) {
          this.setBlurState("off");
          return;
        }
        this.setBlurState(segmenter ? "ready" : "unavailable");
      });
  }

  /** Turns one segmentation result into a white-on-transparent alpha mask ready to composite.
   * Returns the previous mask unchanged if this frame produced nothing usable. */
  private updateMask(): HTMLCanvasElement | null {
    const segmenter = this.segmenter;
    if (!segmenter) return this.maskCanvas;

    const result = segmenter.segment(this.cameraVideo, performance.now());
    const mask = result?.categoryMask;
    if (!result || !mask) {
      result?.close();
      return this.maskCanvas;
    }

    try {
      const { width, height } = mask;
      const categories = mask.getAsUint8Array();
      if (width <= 0 || height <= 0 || categories.length < width * height) return this.maskCanvas;

      if (!this.maskCanvas) this.maskCanvas = document.createElement("canvas");
      const canvas = this.maskCanvas;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return this.maskCanvas;

      // Opaque white over the person, fully transparent over the background. The colour is
      // irrelevant — only the alpha channel survives the `destination-in` composite that uses
      // this. See CATEGORY_PERSON for why the comparison is against the person rather than the
      // background: this model marks the person with 0 and the background with 255, and
      // reading it the other way round blurs the subject instead of the room.
      const image = ctx.createImageData(width, height);
      const pixels = image.data;
      for (let i = 0; i < width * height; i++) {
        const alpha = categories[i] === CATEGORY_PERSON ? 255 : 0;
        const p = i * 4;
        pixels[p] = 255;
        pixels[p + 1] = 255;
        pixels[p + 2] = 255;
        pixels[p + 3] = alpha;
      }
      ctx.putImageData(image, 0, 0);
      return canvas;
    } finally {
      result.close();
    }
  }

  // ---- drawing --------------------------------------------------------------------------

  /**
   * Renders the camera — source rect `(sx, sy, sw, sh)` of the camera frame — into a scratch
   * canvas of `dw` x `dh` with the active filter applied, and returns it. Returns `null` when
   * nothing should be drawn.
   *
   * Background blur is the two-layer trick: a blurred copy of the whole region, then the sharp
   * copy masked down to the person drawn back on top. The mask is feathered on the way in
   * (`ctx.filter` on the mask draw) because the model's category mask is hard 0/1 and its
   * unfeathered edge reads as a cut-out.
   */
  private renderCamera(
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dw: number,
    dh: number,
  ): HTMLCanvasElement | null {
    if (dw <= 0 || dh <= 0) return null;
    const camera = this.cameraVideo;
    const vw = camera.videoWidth;
    const vh = camera.videoHeight;
    if (!vw || !vh) return null;

    const option = cameraFilterOption(this.settings.filter);
    const ctx = this.filtered.sized(dw, dh);

    const mask = option.segmented ? this.updateMask() : null;
    if (option.segmented && mask) {
      ctx.filter = `blur(${Math.max(1, Math.round(dh * BACKGROUND_BLUR_FRACTION))}px)`;
      ctx.drawImage(camera, sx, sy, sw, sh, 0, 0, dw, dh);
      ctx.filter = "none";

      const personCtx = this.person.sized(dw, dh);
      personCtx.drawImage(camera, sx, sy, sw, sh, 0, 0, dw, dh);
      personCtx.globalCompositeOperation = "destination-in";
      personCtx.filter = `blur(${Math.max(1, Math.round(dh * MASK_FEATHER_FRACTION))}px)`;
      // The mask covers the whole camera frame, so the same crop has to be taken from it, in
      // its own (model-output) resolution.
      personCtx.drawImage(
        mask,
        (sx / vw) * mask.width,
        (sy / vh) * mask.height,
        (sw / vw) * mask.width,
        (sh / vh) * mask.height,
        0,
        0,
        dw,
        dh,
      );
      personCtx.filter = "none";
      personCtx.globalCompositeOperation = "source-over";

      ctx.drawImage(this.person.canvas, 0, 0);
      return this.filtered.canvas;
    }

    // Everything else — including background blur while the segmenter is still loading or
    // after it failed — is one pass with an optional CSS filter string.
    ctx.filter = option.css ?? "none";
    ctx.drawImage(camera, sx, sy, sw, sh, 0, 0, dw, dh);
    ctx.filter = "none";
    return this.filtered.canvas;
  }

  private drawFrame(): void {
    const { canvas, ctx } = this;
    const camera = this.cameraVideo;
    const vw = camera.videoWidth;
    const vh = camera.videoHeight;

    if (!this.screenVideo) {
      // Camera-only: the canvas is the camera's own size, so this is a straight 1:1 blit of
      // the filtered frame. Not mirrored — the raw camera track was not mirrored before this
      // mode was composited, and flipping it now would silently change what people record.
      const rendered = vw && vh ? this.renderCamera(0, 0, vw, vh, canvas.width, canvas.height) : null;
      if (rendered) ctx.drawImage(rendered, 0, 0);
      this.onDraw?.(rendered !== null);
      return;
    }

    ctx.drawImage(this.screenVideo, 0, 0, canvas.width, canvas.height);
    // The screen was drawn, so the canvas produces a frame whatever happens to the bubble.
    this.onDraw?.(true);
    if (!vw || !vh) return;

    const bubble = clampBubble(this.settings.bubble, this.aspect);
    const r = Math.round(canvas.height * bubble.radiusFraction);
    const cx = Math.round(canvas.width * bubble.x);
    const cy = Math.round(canvas.height * bubble.y);
    if (r <= 0) return;

    // Centered square crop, so the circle shows the middle of the camera rather than a
    // squashed full frame.
    const side = Math.min(vw, vh);
    const rendered = this.renderCamera((vw - side) / 2, (vh - side) / 2, side, side, r * 2, r * 2);
    if (!rendered) return;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.translate(cx, cy);
    ctx.scale(-1, 1); // mirrored, like a normal self-view camera preview
    ctx.drawImage(rendered, -r, -r, r * 2, r * 2);
    ctx.restore();
  }
}
